import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {ListEmptyState} from '@/components/section-card'
import {useReportesHistorial, type ReporteHistorial} from '@/hooks/bateria/use-bateria-reportes'
import {formatDateTime} from '@/lib/risk'

const STATUS_TONE: Record<ReporteHistorial['status'], 'neutral' | 'amber' | 'green' | 'red'> = {
  pendiente: 'amber',
  procesando: 'amber',
  listo: 'green',
  error: 'red',
}

function AlcanceLabel({alcance}: {alcance: ReporteHistorial['alcance']}) {
  const {t} = useTranslation()
  return (
    <span>
      {alcance === 'consolidado'
        ? t('admin.bateria.informe.acciones.historial.consolidado')
        : t('admin.bateria.formaValue', {forma: alcance})}
    </span>
  )
}

function HistorialFila({fila}: {fila: ReporteHistorial}) {
  const {t} = useTranslation()
  return (
    <li
      className="surface-divider flex flex-wrap items-start justify-between gap-2 border-b py-3 last:border-0"
      data-testid="reporte-historial-fila">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-foreground">
            <AlcanceLabel alcance={fila.alcance} />
          </span>
          <Tag tone={STATUS_TONE[fila.status]}>{t(`admin.bateria.reporte.status.${fila.status}`)}</Tag>
        </div>
        <p className="mt-0.5 font-mono text-xs text-muted-foreground">
          {fila.generado_en ? formatDateTime(fila.generado_en) : '—'}
          {fila.generado_por_name && <> · {fila.generado_por_name}</>}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t('admin.bateria.informe.acciones.historial.nValidados', {count: fila.n_validados})}
          {fila.motor_sha && <> · {t('admin.bateria.informe.acciones.historial.motorSha', {sha: fila.motor_sha})}</>}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
        {fila.download_url_docx && (
          <a
            href={fila.download_url_docx}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
            data-testid="reporte-historial-docx">
            {t('admin.bateria.informe.acciones.historial.docx')}
          </a>
        )}
        {fila.download_url_pdf && (
          <a
            href={fila.download_url_pdf}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
            data-testid="reporte-historial-pdf">
            {t('admin.bateria.informe.acciones.historial.pdf')}
          </a>
        )}
      </div>
    </li>
  )
}

/** Todas las generaciones del lote, recientes primero. El PDF sale vacío en v1: el DOCX es el
 * entregable canónico. */
export function ReporteHistorialDialog({
  businessId,
  evaluacionId,
  open,
  onOpenChange,
}: {
  businessId: string
  evaluacionId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const {data, isLoading} = useReportesHistorial(businessId, evaluacionId, open)
  const items = data?.items ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reporte-historial-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.bateria.informe.acciones.historial.title')}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <Skeleton className="h-32 w-full" data-testid="reporte-historial-skeleton" />
        ) : items.length === 0 ? (
          <ListEmptyState title={t('admin.bateria.informe.acciones.historial.empty')} />
        ) : (
          <ul data-testid="reporte-historial-lista">
            {items.map((fila) => (
              <HistorialFila key={fila.id} fila={fila} />
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  )
}
